// Account users: up to 3 extra logins the account owner hands out, each limited to a purpose
// (reports only / campaigns & lists / full). Rendered inside the profile page, and only for the
// owner — a sub-user never sees or reaches this card (the backend refuses the endpoints too).
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Delete from "@material-ui/icons/Delete";
import VpnKey from "@material-ui/icons/VpnKey";
import Group from "@material-ui/icons/Group";
import SweetAlert from "react-bootstrap-sweetalert";
import { CallApi } from 'utils/Request';
import Toast from 'views/Toast/Toast';

const EMPTY = { email: '', name: '', password: '', sub_role: 'reports' };

export default function SubUsersCard({ cardIconTitleClass }) {
  const { t } = useTranslation();
  const user = useSelector(s => s.user);
  const [rows, setRows] = useState([]);
  const [limit, setLimit] = useState(3);
  const [form, setForm] = useState(EMPTY);
  const [alert, setAlert] = useState(null);

  const ROLES = [
    { v: 'reports', label: t('Reports & statistics only') },
    { v: 'campaigns', label: t('Campaigns & lists only') },
    { v: 'full', label: t('Full access') },
  ];
  const roleLabel = (v) => { const r = ROLES.find(x => x.v === v); return r ? r.label : v; };

  const load = () => CallApi({ url: 'user/sub_users', method: 'get' }).then(res => {
    if (res.success) { setRows(res.data || []); if (res.limit) setLimit(res.limit); }
  });
  useEffect(() => { load(); }, []);

  // the card is for the account owner only
  if (user.profile && user.profile.sub_role) return null;

  const errText = (code) => ({
    invalid_email: t('Please enter a valid email address'),
    password_too_short: t('Password must be at least 6 characters'),
    email_taken: t('This email is already in use'),
    sub_user_limit: t('You have reached the limit of extra users'),
    not_found: t('User not found'),
  }[code] || code);

  const create = () => {
    CallApi({ url: 'user/sub_user_create', method: 'post', data: form }).then(res => {
      if (res.success) { setForm(EMPTY); Toast(200, t('User created')); load(); }
      else Toast(400, errText(res.error));
    });
  };
  const update = (id, data, okMsg) => {
    CallApi({ url: 'user/sub_user_update', method: 'post', data: { id, ...data } }).then(res => {
      if (res.success) { if (okMsg) Toast(200, okMsg); load(); }
      else Toast(400, errText(res.error));
    });
  };
  const confirmDelete = (row) => setAlert(
    <SweetAlert warning showCancel
      title={t('Delete this user?')}
      confirmBtnText={t('Delete')} cancelBtnText={t('Cancel')}
      onConfirm={() => {
        setAlert(null);
        CallApi({ url: 'user/sub_user_delete', method: 'post', data: { id: row.id } }).then(res => {
          if (res.success) { Toast(200, t('Deleted successfully')); load(); }
          else Toast(400, errText(res.error));
        });
      }}
      onCancel={() => setAlert(null)}
    >{row.email}</SweetAlert>
  );
  const askNewPassword = (row) => {
    const pass = window.prompt(t('New password for') + ' ' + row.email);
    if (pass === null) return;
    update(row.id, { password: pass }, t('Password updated'));
  };

  const selectStyle = { fontFamily: 'inherit', fontSize: 13, padding: '7px 10px', border: '1px solid #cfd8d3', borderRadius: 8, background: '#fff', cursor: 'pointer' };

  return (
    <Card>
      {alert}
      <CardHeader color="success" icon>
        <CardIcon color="success"><Group /></CardIcon>
        <h4 className={cardIconTitleClass}>{t('Account users')} ({rows.length}/{limit})</h4>
      </CardHeader>
      <CardBody>
        <p style={{ color: '#5d6d66', fontSize: 13.5 }}>
          {t('Extra logins for your team, each limited to a purpose. They sign in at the normal login page with their own email and password.')}
        </p>
        {rows.map(row => (
          <div key={row.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderBottom: '1px solid #eef2f0', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{row.email}{row.name ? <span style={{ color: '#8a988f', fontWeight: 400 }}> — {row.name}</span> : null}</div>
              <div style={{ fontSize: 12, color: '#8a988f' }}>{roleLabel(row.sub_role)}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <select value={row.sub_role} onChange={e => update(row.id, { sub_role: e.target.value }, t('Role updated'))} style={selectStyle}>
                {ROLES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
              <Tooltip title={t('Active')}>
                <Switch checked={!!row.active} color="primary" onChange={() => update(row.id, { active: !row.active })} />
              </Tooltip>
              <Tooltip title={t('Change password')}>
                <a onClick={() => askNewPassword(row)} style={{ cursor: 'pointer' }}><VpnKey color="primary" /></a>
              </Tooltip>
              <Tooltip title={t('Delete')}>
                <a onClick={() => confirmDelete(row)} style={{ cursor: 'pointer' }}><Delete color="error" /></a>
              </Tooltip>
            </div>
          </div>
        ))}

        {rows.length < limit && (
          <GridContainer style={{ marginTop: 8 }}>
            <GridItem sm={3} xs={12}>
              <CustomInput labelText={t('Email')} formControlProps={{ fullWidth: true }}
                inputProps={{ type: 'email', value: form.email, onChange: e => setForm({ ...form, email: e.target.value }) }} />
            </GridItem>
            <GridItem sm={3} xs={12}>
              <CustomInput labelText={t('Name')} formControlProps={{ fullWidth: true }}
                inputProps={{ type: 'text', value: form.name, onChange: e => setForm({ ...form, name: e.target.value }) }} />
            </GridItem>
            <GridItem sm={2} xs={12}>
              <CustomInput labelText={t('Password')} formControlProps={{ fullWidth: true }}
                inputProps={{ type: 'password', value: form.password, onChange: e => setForm({ ...form, password: e.target.value }) }} />
            </GridItem>
            <GridItem sm={2} xs={12} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 10 }}>
              <select value={form.sub_role} onChange={e => setForm({ ...form, sub_role: e.target.value })} style={{ ...selectStyle, width: '100%' }}>
                {ROLES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </GridItem>
            <GridItem sm={2} xs={12} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button color="info" disabled={!form.email || !form.password} onClick={create}>{t('Add user')}</Button>
            </GridItem>
          </GridContainer>
        )}
        {rows.length >= limit && (
          <p style={{ color: '#8a988f', fontSize: 12.5, marginTop: 10 }}>{t('You have reached the limit of extra users')}.</p>
        )}
      </CardBody>
    </Card>
  );
}
